import type React from 'react'
import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { SidebarPadding, SidebarPosition } from './Sidebar.h'

import { useEffect, useMemo, useRef, useState } from 'react'
import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import { useWindowResize } from 'utils/hooks'
import { FIXED_INDENTS } from 'vars/fixed-indents'
import { SidebarPaddings } from './Sidebar.h'
import { SidebarBodyStyled, SidebarStyled } from './Sidebar.styled'

export interface ISidebarProps {
  children: React.ReactNode
  content: object[]
  title?: ITextBoxProps['title']
  paddings?: SidebarPadding
  rowGap?: FIXED_INDENTS
  width?: string
  height?: string
  position?: SidebarPosition
}

export const Sidebar = ({
  children,
  content,
  title = '',
  width,
  height = '100vh',
  paddings = SidebarPaddings.SIZE_0,
  rowGap = FIXED_INDENTS.INDENT_40,
  position,
}: ISidebarProps): JSX.Element | null => {
  const ref = useRef<HTMLSpanElement>(null)
  const [titleHeight, setTitleHeight] = useState(0)

  useEffect(() => {
    const height = ref?.current?.clientHeight
    if (height) setTitleHeight(height)
  }, [ref])

  useWindowResize((): void => {
    const height = ref?.current?.clientHeight
    if (height) setTitleHeight(height)
  })

  const sidebarBodyHeight = useMemo(() => {
    const indent = FIXED_INDENTS.INDENT_0
    return `${height} - ${titleHeight}px - var(${
      paddings?.[0] ?? indent
    }) - var(${paddings?.[2] ?? indent})`
  }, [height, titleHeight, paddings])

  return content?.length > 0 ? (
    <SidebarStyled
      width={width}
      height={height}
      paddings={paddings}
      position={position}
    >
      {title ? (
        <TextBox
          ref={ref}
          title={title}
          font={TextBoxTypes.Headline4}
          color={TextBoxColors.TEXT_ICON_PRIMARY}
          maxLines={1}
        />
      ) : null}
      <SidebarBodyStyled rowGap={rowGap} sidebarBodyHeight={sidebarBodyHeight}>
        {children}
      </SidebarBodyStyled>
    </SidebarStyled>
  ) : null
}
